<template>
    <div class="bg-purple-50 font-body">
        <header class="bg-purple-500 text-white py-6 shadow-lg">
            <div class="container mx-auto px-4">
                <div class="flex flex-col md:flex-row justify-between items-center">
                    <div class="text-center md:text-left mb-4 md:mb-0">
                        <h1 class="restaurant-font text-white text-3xl md:text-4xl font-bold">Gelateria Real</h1>
                        <p class="restaurant-font text-purple-200 italic font-bold">Sabor & Diversão</p>
                    </div>
                </div>
            </div>
        </header>

        <section class="bg-purple-100 py-4 shadow-sm sticky top-0 z-30">
            <div class="overflow-x-auto px-4">
                <div class="flex gap-2 whitespace-nowrap justify-center">
                    <button v-for="cat in dynamicCategories" :key="cat.value" @click="selectedCategory = cat.value" :class="[
                        'px-4 py-2 rounded-full font-bold transition-colors duration-300',
                        selectedCategory === cat.value
                            ? 'bg-purple-500 text-white'
                            : 'bg-white text-purple-500 border border-purple-300 hover:bg-purple-200'
                    ]">
                        {{ cat.label }}
                    </button>
                </div>
            </div>
        </section>

        <main class="container mx-auto px-4 py-12">
            <div v-if="loadingScreen" class="flex flex-col items-center justify-center py-16 text-purple-500 font-bold text-xl">
                <svg class="animate-spin h-10 w-10 text-purple-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                </svg>
                <span>Carregando cardápio...</span>
            </div>

            <div v-else-if="filteredDishes.length === 0" class="text-center py-16 text-zinc-600 font-bold text-xl">
                Nenhum produto disponível no momento. Volte em breve!
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div v-for="dish in filteredDishes" :key="dish.id" class="dish-card bg-white rounded-lg shadow-md overflow-hidden flex flex-col transition-all duration-300 hover:shadow-xl">
                    <div class="relative overflow-hidden h-56 flex items-center justify-center bg-purple-100">
                        <img :src="dish.image" :alt="dish.name" class="dish-image w-auto max-h-full object-contain" />
                        <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div class="absolute bottom-0 left-0 p-4">
                            <h3 class="restaurant-font text-white text-2xl font-bold drop-shadow">{{ dish.name }}</h3>
                            <p class="text-purple-300 font-bold">{{ dish.price }}</p>
                        </div>
                    </div>

                    <div class="p-4 flex flex-col flex-1">
                        <p class="text-zinc-800 mb-4 line-clamp-3">{{ dish.description }}</p>
                        <div class="mt-auto">
                            <button @click="openModal(dish.name)" class="w-full py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-full transition-colors duration-300">
                                Ver detalhes
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </main>

        <!-- Modal -->
        <div v-if="modalDish" class="dish-modal fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" @click.self="closeModal">
            <div class="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-lg relative flex flex-col">
                <!-- Botão Fechar -->
                <button @click="closeModal" class="absolute top-4 right-4 bg-purple-500 hover:bg-purple-600 text-white w-10 h-10 rounded-full flex items-center justify-center z-10">
                    <i class="fas fa-times"></i>
                </button>

                <div class="flex-1 overflow-y-auto">
                    <div class="grid grid-cols-1 md:grid-cols-2">
                        <!-- Imagem -->
                        <div class="flex items-center justify-center bg-purple-100 p-4">
                            <img :src="modalDish.image" :alt="modalDish.name" class="max-h-80 w-auto object-contain">
                        </div>

                        <div class="p-6">
                            <h2 class="restaurant-font text-2xl md:text-3xl font-bold text-zinc-800 mb-2">{{ modalDish.name }}</h2>
                            <p class="text-purple-500 text-lg md:text-xl font-bold mb-4">{{ modalDish.price }}</p>

                            <div v-if="modalDish.description" class="mb-4">
                                <h3 class="text-lg font-semibold mb-1 text-zinc-800">Descrição</h3>
                                <p class="text-zinc-600">{{ modalDish.description }}</p>
                            </div>

                            <div v-if="modalDish.preparation" class="mb-4">
                                <h3 class="text-lg font-semibold mb-1 text-zinc-800">Modo de Preparo</h3>
                                <p class="text-zinc-600">{{ modalDish.preparation }}</p>
                            </div>

                            <div v-if="modalDish.pairing">
                                <h3 class="text-lg font-semibold mb-1 text-zinc-800">Harmonização</h3>
                                <p class="text-zinc-600">{{ modalDish.pairing }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <footer class="bg-purple-500 text-white py-8">
            <div class="container mx-auto px-4 text-center">
                <h2 class="text-white text-2xl mb-2 font-bold">Gelateria Real</h2>
                <p class="text-white text-sm font-bold">© {{ new Date().getFullYear() }} Resort Eco Business. Todos os direitos reservados.</p>
            </div>
        </footer>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from "pinia";
import { useProdutoStore } from '@/stores/produto';
import { mysqlToReal } from "@/helpers/Currency";
import { capitalize } from "@/helpers/String";

const produtoStore = useProdutoStore();
const { produtos } = storeToRefs(produtoStore);
const selectedCategory = ref('all');
const modalDish = ref(null);
const loadingScreen = ref(true);
const cardapioList = ref([]);
const dynamicCategories = ref([]);

onMounted(async () => {
    await getCardapio();
    buildCategories();
});

async function getCardapio() {
    try {
        const lojaId = 5;
        loadingScreen.value = true;

        await produtoStore.listarProdutos(lojaId);

        cardapioList.value = (produtos.value || []).map(produto => {
            return {
                id: produto.produto_loja_id,
                name: produto.nome,
                category: produto.categoria,
                price: "R$ " + mysqlToReal(produto.valor),
                image: produto.imagem,
                description: produto.descricao_loja,
            };
        });

        console.log("produtos", cardapioList.value);
    } catch (error) {
        console.error("Erro ao buscar produto:", error);
    } finally {
        loadingScreen.value = false;
    }
}

function buildCategories() {
    const found = new Set();
    dynamicCategories.value = [{ label: 'Todos', value: 'all' }];

    cardapioList.value.forEach(produto => {
        if (!found.has(produto.category)) {
            dynamicCategories.value.push({
                label: capitalize(produto.category),
                value: produto.category
            });

            found.add(produto.category);
        }
    });
}

const filteredDishes = computed(() => {
    return selectedCategory.value === 'all'
        ? cardapioList.value
        : cardapioList.value.filter(dish => dish.category === selectedCategory.value);
});

function openModal(dishName) {
    modalDish.value = cardapioList.value.find(d => d.name === dishName);
    document.body.style.overflow = 'hidden';
}

function closeModal() {
    modalDish.value = null;
    document.body.style.overflow = 'auto';
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Baloo+2&display=swap');

.restaurant-font {
    font-family: 'Baloo 2', cursive;
}

.dish-modal {
    transition: all 0.3s ease;
}

.dish-modal img {
    object-fit: contain;
    width: 100%;
    height: 100%;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

.dish-card:hover .dish-image {
    transform: scale(1.03);
}

.dish-image {
    transition: transform 0.3s ease;
    width: 100%;
    height: 100%;
}

.line-clamp-3 {
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.dish-card {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.dish-card .p-4 {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.container::-webkit-scrollbar {
    display: none;
}
</style>